import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { PackageFields, type PackageFormState } from '@openx/types/package';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckBoxWithTooltip } from './CheckBoxWithTooltip';

type AttentionProps = { disabled?: boolean };
type AttentionCheckboxesState = {
  attention: boolean;
  vpvh: boolean;
};

const vpvhFiledName = `${PackageFields.TARGETING}.${TargetingFields.VPVH}` as const;
const attentionFiledName = `${PackageFields.TARGETING}.${TargetingFields.ATTENTION}` as const;

export const Attention = ({ disabled }: AttentionProps) => {
  const { t } = useTranslation();
  const [disabledCheckboxes, setDisabledCheckboxes] = useState<AttentionCheckboxesState>({
    attention: !!disabled,
    vpvh: !!disabled,
  });

  const { setValue, control } = useFormContext<PackageFormState>();
  const observedAttentionCheckbox = useWatch<PackageFormState, typeof attentionFiledName>({
    name: attentionFiledName,
    control,
  });
  const observedVpvhCheckbox = useWatch<PackageFormState, typeof vpvhFiledName>({
    name: vpvhFiledName,
    control,
  });

  const handleCheckboxChange = (name: string, value: boolean) => {
    if (name === attentionFiledName) {
      const valueToSet = value ? '1' : null;
      setValue(attentionFiledName, valueToSet, { shouldDirty: true, shouldValidate: true });
      setValue(vpvhFiledName, null, { shouldDirty: true, shouldValidate: true });
    } else if (name === vpvhFiledName) {
      const valueToSet = value ? 'true' : null;
      setValue(vpvhFiledName, valueToSet, { shouldDirty: true, shouldValidate: true });
      setValue(attentionFiledName, null, { shouldDirty: true, shouldValidate: true });
    }
  };

  useEffect(() => {
    if (disabled) {
      setValue(attentionFiledName, null, { shouldDirty: true, shouldValidate: true });
      setValue(vpvhFiledName, null, { shouldDirty: true, shouldValidate: true });
      setDisabledCheckboxes({ attention: true, vpvh: true });
    } else {
      const isAttentionDisabled = observedVpvhCheckbox === 'true';
      const isVpvhDisabled = observedAttentionCheckbox === '1';

      setDisabledCheckboxes({ attention: isAttentionDisabled, vpvh: isVpvhDisabled });
    }
  }, [disabled, observedAttentionCheckbox, observedVpvhCheckbox, setValue]);

  return (
    <Box
      data-test="targeting-attention"
      sx={{
        my: 2,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          width: '100%',
          mb: 1.5,
          display: 'flex',
          gap: 1,
        }}
      >
        {t('CTV Attention metrics')}
      </Typography>
      <CheckBoxWithTooltip
        name={attentionFiledName}
        valueOfChecked="1"
        label={t('Target content with highly attentive audiences')}
        tooltipText={t(
          'Applies only for CTV inventory. Utilizes TVision’s passive, in-home, person-level panel, and predictive attention models to target content with above average attention scores.'
        )}
        disabled={disabledCheckboxes.attention}
        onChange={handleCheckboxChange}
      />
      <CheckBoxWithTooltip
        name={vpvhFiledName}
        valueOfChecked="true"
        label={t('Target segments with high likelihood of co-viewing')}
        tooltipText={t(
          'Applies only for CTV inventory. Utilizes TVision’s passive, in-home, person-level panel and predictive attention models to target content with high likelihood of co-viewing.'
        )}
        disabled={disabledCheckboxes.vpvh}
        onChange={handleCheckboxChange}
      />
    </Box>
  );
};
